import React from 'react';
import { motion } from 'framer-motion';
import { 
  Stethoscope, 
  Shield, 
  Lock, 
  Database, 
  Cloud,
  FileCheck,
  Network,
  Users,
  CheckCircle,
  ArrowRight,
  Server,
  AlertTriangle
} from 'lucide-react';

interface HealthcareProps {
  setCurrentPage: (page: string) => void;
}

const Healthcare: React.FC<HealthcareProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "HIPAA Compliance",
      description: "Comprehensive security solutions ensuring full HIPAA compliance and patient data protection.",
      items: [
        "Privacy rule compliance",
        "Security rule implementation",
        "Breach notification protocols",
        "Regular compliance audits"
      ]
    },
    {
      icon: Lock,
      title: "Patient Data Security",
      description: "Advanced protection measures for electronic health records and sensitive patient information.",
      items: [
        "End-to-end encryption",
        "Access control management",
        "Data loss prevention",
        "Secure data sharing"
      ]
    },
    {
      icon: Network,
      title: "Medical Device Security",
      description: "Specialized security for connected medical devices and healthcare IoT infrastructure.",
      items: [
        "Device authentication",
        "Network segmentation",
        "Vulnerability management",
        "Real-time monitoring"
      ]
    },
    {
      icon: Cloud,
      title: "Telemedicine Security",
      description: "Secure solutions for remote healthcare delivery and virtual patient consultations.",
      items: [
        "Secure video conferencing",
        "Patient portal security",
        "Remote access protection",
        "Communication encryption"
      ]
    }
  ];

  const solutions = [
    {
      icon: Database,
      title: "Healthcare Data Protection",
      items: [
        "Electronic Health Records (EHR) security",
        "Patient data encryption",
        "Secure backup systems",
        "Data access controls"
      ]
    },
    {
      icon: Server,
      title: "Infrastructure Security",
      items: [
        "Network security",
        "Cloud infrastructure protection",
        "Endpoint security",
        "Security monitoring"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      items: [
        "Risk assessment",
        "Compliance monitoring",
        "Incident response",
        "Security audits"
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Enhanced Protection",
      description: "Comprehensive security coverage protecting patient data and healthcare operations."
    },
    {
      icon: FileCheck,
      title: "Regulatory Compliance",
      description: "Ensure compliance with HIPAA and other healthcare security regulations."
    },
    {
      icon: Users,
      title: "Patient Trust",
      description: "Build and maintain patient trust through robust data protection measures."
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Stethoscope className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Healthcare Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Comprehensive cybersecurity solutions designed specifically for healthcare organizations, protecting patient data and ensuring HIPAA compliance
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Security Solutions</h2>
            <p className="text-lg text-muted">
              End-to-end security solutions for healthcare organizations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {solution.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
            <p className="text-lg text-muted">
              Why choose our healthcare security solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 text-center hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mx-auto mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Healthcare Organization</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our healthcare security experts to discuss your organization's specific needs and discover how we can help protect your patients' data
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Healthcare;